import { api } from "@/lib/api";

interface OnboardingData {
  productName: string;
  productDescription: string;
  targetAudience: string;
  painPoints?: string;
  callToAction: string;
  userName: string;
  email: string;
  password: string;
  noFreeTrial?: boolean;
}

interface StripeTokenData {
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
}

export const onboardingService = {
  // Store campaign data temporarily until user is created
  campaignDataCache: null as Record<string, unknown> | null,

  // Simple signup without campaign data
  async simpleSignup(data: {
    userName: string;
    email: string;
    password: string;
    planId: string;
    noFreeTrial?: boolean;
  }) {
    try {
      // Get plan details based on selected plan
      const planId = data.planId || "growth";
      const planDetails = {
        growth: {
          name: "Autonomous AI Marketer",
          price: 97,
          whopPlanKey: "AUTONOMOUS_AI_MARKETER",
        },
        pro: {
          name: "Pro AI Marketer",
          price: 297,
          whopPlanKey: "PRO_AI_MARKETER",
        },
      } as const;

      const selectedPlan = planDetails[planId as keyof typeof planDetails];

      // Store plan details for Facebook Pixel tracking after redirect
      localStorage.setItem("selectedPlanName", selectedPlan.name);
      localStorage.setItem("selectedPlanPrice", selectedPlan.price.toString());
      localStorage.setItem("selectedPlanId", planId);

      // Save pending credentials to enable auto-login after redirect
      localStorage.setItem("pendingStripeEmail", data.email);
      localStorage.setItem("pendingStripePassword", data.password);

      // Whop: initiate checkout (backend returns hosted URL)
      const response = await api.post("/auth/initiate-whop-checkout", {
        email: data.email,
        planKey: selectedPlan.whopPlanKey,
        metadata: { source: "landing_simple_onboarding" },
      });

      // If we get a checkout URL, redirect to Stripe
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return { redirecting: true };
      }

      throw new Error("No checkout URL received");
    } catch (error: unknown) {
      console.error("Simple signup error:", error);
      const e = error as { response?: { data?: { error?: string } } };
      if (e.response?.data?.error) {
        throw new Error(e.response.data.error);
      }
      throw error;
    }
  },

  // Step 1: Prepare campaign data (don't create yet since we need auth)
  async prepareCampaign(data: {
    productName: string;
    productDescription: string;
    targetAudience: string;
    painPoints?: string;
    callToAction: string;
  }) {
    // Cache the campaign data for later creation
    this.campaignDataCache = {
      name: `${data.productName} - Auto Campaign`,
      targetAudience: data.targetAudience,
      emailTemplate: "ai-generated", // This should match your template IDs
      status: "draft",
      product: data.productName,
      volumeSettings: {
        dailyTarget: 50,
        hourlyLimit: 10,
        totalBudget: 1000,
      },
      // Store additional data for AI generation
      productDescription: data.productDescription,
      painPoints: data.painPoints || "",
      callToAction: data.callToAction,
    };

    return { id: "pending", ...this.campaignDataCache };
  },

  // Create campaign after authentication
  async createCampaignWithAuth() {
    if (!this.campaignDataCache) {
      throw new Error("No campaign data available");
    }

    try {
      const response = await api.post("/campaigns", this.campaignDataCache);
      return response.data;
    } catch (error) {
      console.error("Error creating campaign:", error);
      throw new Error("Failed to create campaign. Please try again.");
    }
  },

  // Step 2: Register user
  async registerUser(email: string, password: string, campaignId?: string) {
    try {
      // First try the auth/register endpoint
      const response = await api.post("/auth/register", {
        email,
        password,
        campaignId, // Link the campaign to the new user
      });

      // Store auth token if provided
      if (response.data.token) {
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error: unknown) {
      console.error("Error registering user:", error);
      const e = error as { response?: { status?: number } };
      if (e.response?.status === 409) {
        throw new Error(
          "An account with this email already exists. Please log in instead."
        );
      }
      throw new Error("Failed to create account. Please try again.");
    }
  },

  // Step 3: Initialize Stripe checkout for trial
  async initializeStripeCheckout(email: string, planKey: string = "growth") {
    try {
      const response = await api.post("/auth/initiate-stripe-checkout", {
        email,
        planKey,
        trial: true,
        successUrl: `https://auth.munger.ai/#/overview?onboarding=complete`,
        cancelUrl: `${window.location.origin}/#/new?onboarding=cancelled`,
      });

      return response.data;
    } catch (error) {
      console.error("Error initializing Stripe checkout:", error);
      throw new Error("Failed to set up payment method. Please try again.");
    }
  },

  // Alternative: Create subscription with token (if using Stripe Elements)
  async createSubscription(token: string, planKey: string = "growth") {
    try {
      const response = await api.post("/subscriptions/create", {
        paymentToken: token,
        planKey,
        trial: true,
      });

      return response.data;
    } catch (error) {
      console.error("Error creating subscription:", error);
      throw new Error("Failed to start trial. Please try again.");
    }
  },

  // Launch the campaign after setup
  async launchCampaign(campaignId: string) {
    try {
      const response = await api.post(`/campaigns/${campaignId}/execute`);
      return response.data;
    } catch (error) {
      console.error("Error launching campaign:", error);
      throw new Error("Failed to launch campaign. Please try again.");
    }
  },

  // Complete onboarding flow
  async completeOnboarding(data: OnboardingData & { planId?: string }) {
    try {
      // 1. First prepare the campaign data (no API call yet)
      await this.prepareCampaign({
        productName: data.productName,
        productDescription: data.productDescription,
        targetAudience: data.targetAudience,
        painPoints: data.painPoints,
        callToAction: data.callToAction,
      });

      // Get plan details based on selected plan
      const planId = data.planId || "growth";
      const planDetails = {
        growth: {
          name: "Growth Plan",
          price: 97,
        },
        pro: {
          name: "Pro Plan",
          price: 297,
        },
      };

      const selectedPlan = planDetails[planId as keyof typeof planDetails];

      // Store plan details for Facebook Pixel tracking after redirect
      localStorage.setItem("selectedPlanName", selectedPlan.name);
      localStorage.setItem("selectedPlanPrice", selectedPlan.price.toString());
      localStorage.setItem("selectedPlanId", planId);

      // 2. Use the combined register + checkout endpoint
      const response = await api.post("/auth/initiate-stripe-checkout", {
        email: data.email,
        password: data.password,
        name: data.userName,
        planId: planId,
        planName: selectedPlan.name,
        planPrice: selectedPlan.price,
        planInterval: "month",
        trial: !data.noFreeTrial, // If noFreeTrial is true, trial is false
        // Redirect to login page after successful payment
        successUrl: `https://auth.munger.ai/#/login?checkout=success&email=${encodeURIComponent(
          data.email
        )}`,
      });

      // 3. If we get a checkout URL, redirect to Stripe
      if (response.data.checkoutUrl) {
        // Store campaign data in localStorage to create after payment
        localStorage.setItem(
          "pending_campaign",
          JSON.stringify(this.campaignDataCache)
        );

        window.location.href = response.data.checkoutUrl;
        return { redirecting: true };
      }

      throw new Error("No checkout URL received");
    } catch (error: unknown) {
      console.error("Onboarding error:", error);
      const e = error as { response?: { data?: { error?: string } } };
      if (e.response?.data?.error) {
        throw new Error(e.response.data.error);
      }
      throw error;
    }
  },

  // Create campaign after successful payment (call this from dashboard)
  async createPendingCampaign() {
    const pendingData = localStorage.getItem("pending_campaign");
    if (!pendingData) return null;

    try {
      const campaignData = JSON.parse(pendingData);
      this.campaignDataCache = campaignData;

      const campaign = await this.createCampaignWithAuth();

      // Clear the pending data
      localStorage.removeItem("pending_campaign");

      // Launch the campaign
      await this.launchCampaign(campaign.id);

      return campaign;
    } catch (error) {
      console.error("Error creating pending campaign:", error);
      throw error;
    }
  },
};
